import { useState, useEffect, Fragment, useMemo, memo } from 'react';
import AddLog from '../AddLog/index.js';
import API from '../../../API/index.js';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useIsMobile } from './useIsMobile.js';
import { convertDateTimeFormat } from '../../../lib/convertDateTimeFormat.js';
import TrackerCarousel from '../../TrackerComponents/TrackerCarousel/index.js';
import FieldTypesView from './FieldTypesView/index.js';
import styles from './Table.module.css';

const formatAddress = (addressObj) => {
    if (!addressObj || typeof addressObj !== 'object') return 'N/A';
    const parts = [];
    if (addressObj.street) parts.push(addressObj.street);
    if (addressObj.city) parts.push(addressObj.city);
    if (addressObj.state && addressObj.postalCode) {
        parts.push(`${addressObj.state} ${addressObj.postalCode}`);
    } else if (addressObj.state) {
        parts.push(addressObj.state);
    } else if (addressObj.postalCode) {
        parts.push(addressObj.postalCode);
    }
    return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const OverlayTriggerWrap = memo(({ data, children }) => {
    const [show, setShow] = useState(false);    

    useEffect(() => {
        if (show) {
            document.body.classList.add(styles.logTooltip);
        } else {
            document.body.classList.remove(styles.logTooltip);
        };
        return () => document.body.classList.remove(styles.logTooltip);
    }, [show]);

    if (data?.length > 0 || data > 0 || Number.isInteger(data)) {
        return (
            <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                trigger={['click', 'hover']}
                overlay={<Tooltip>{data}</Tooltip>}
                container={document.body}
                onToggle={setShow}
            >
                {children}
            </OverlayTrigger>
        );
    } else {
        return <>{children}</>
    };
});

const Log = (props) => {
    const { selectedLog, authenticatedUser, setSelectedLog, selectedSignature, fields, allFields, editLogEntry, setEditLogEntry, qrType, id, configTypes, dataType } = props;
    const [sigCanvas, setSigCanvas] = useState(null);
    const [changeLog, setChangeLog] = useState(selectedLog?.edits ?? {});
    const [showChanges, setShowChanges] = useState(false);

    useEffect(() => {
        sigCanvas && sigCanvas?.clear();
        if (sigCanvas && selectedSignature) {
            sigCanvas?.clear();
            sigCanvas?.fromData(selectedSignature);
            sigCanvas?.off();
        };
    }, [sigCanvas, selectedSignature]);

    useEffect(() => {
        if (selectedLog?.edits) setChangeLog(() => selectedLog?.edits);
    }, [selectedLog, setChangeLog]);

    if (!selectedLog) return <></>;

    if (editLogEntry) {
        return <AddLog id={id} dataType={dataType} configTypes={configTypes} qrType={qrType} fields={allFields} setShowAddLog={setEditLogEntry} editLog={selectedLog} setEditLog={setSelectedLog} />;
    } else {
        return (
            <div className={styles.selectedLogContainer}>
                {selectedLog?.deleted && <div className={styles.deleted}><p>Deleted Log</p></div>}
                {allFields?.map((field, index) => (
                    <FieldTypesView configTypes={configTypes} field={field} selectedLog={selectedLog} sigCanvas={sigCanvas} setSigCanvas={setSigCanvas} key={index} />
                ))}
                {(authenticatedUser && changeLog && Object.keys(changeLog).length > 0) &&
                    <div className={styles.changeLogWrapper}>
                        {showChanges ?
                            <>
                                <div className={styles.changeLogContainer} >
                                    <h4>Edits:</h4>
                                    {Object.keys(changeLog).sort((a, b) => Number(b) - Number(a)).map((date) =>
                                        <div className={styles.changeLogEntry} key={date}>
                                            <p className={styles.changeLogDate}>{new Date(Number(changeLog[date].date_edited))?.toLocaleString()}</p>
                                            {Object.keys(changeLog[date]).map((key, index) => {
                                                if (key === 'date_active' || key === 'date_edited') {
                                                    return <Fragment key={key} ></Fragment>
                                                };

                                                return (
                                                    <div className={styles.changeLogColumn} key={key + index}>
                                                        <p className={styles.changeLogColumnName}>{allFields.find(col => col.id === key)?.name}:</p>
                                                        <p className={styles.changeLogValue}>{changeLog[date][key] ? ((allFields.find(col => col.id === key)?.type === 't9wW' && changeLog[date][key]?.length > 0) ? changeLog[date][key]?.join(', ') : changeLog[date][key]) : 'No Value'}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.btnContainer}>
                                    <span className={styles.changeLog} onClick={() => setShowChanges(false)} >Hide Edits</span>
                                </div>
                            </>
                            :
                            <div className={styles.btnContainer}>
                                <span className={styles.changeLog} onClick={() => setShowChanges(true)} >Show Edits</span>
                            </div>
                        }
                    </div>
                }
            </div>
        );
    };
};

const Table = (props) => {
    const { id, fields, allFields, logs, qrObj, qrData, setShowAddLog, editLogEntry, setEditLogEntry, selectedLog, setSelectedLog, authenticatedUser, deletedToggle, setDeletedToggle, qrType, dataType, configTypes } = props;
    const { getSignature } = API;
    const isMobile = useIsMobile();

    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState('');
    const [selectedSignature, setSelectedSignature] = useState(null);
    const [sort, setSort] = useState({
        field: 'date',
        direction: 'desc'
    });

    // Memoize visible fields and derived widths to avoid recomputation
    const visibleFields = useMemo(() => (fields?.filter(f => f?.type !== 'date' && f?.show !== false) || []), [fields]);
    const columnCount = 1 + (visibleFields?.length || 0); // Timestamp + visible fields
    const columnWidth = useMemo(() => `calc(100% / ${columnCount})`, [columnCount]);

    const handleGetSignature = async (log, show) => {
        setError('');

        const signatureId = fields?.find(col => col?.type === 'PjX1')?.id ?? null;

        if (selectedSignature && selectedSignature?.length > 0) {
            setSelectedSignature(() => null);
        };

        if (show && log[signatureId]) {
            try {
                const res = await getSignature(id, log.date)
                if (res) {
                    setSelectedLog(log);
                    setSelectedSignature(res);
                } else {
                    setError('No Signature Found');
                    setSelectedSignature(null);
                };
            } catch (err) {
                console.error(err);
                setError('Something went wrong, please try again');
            };
        } else if (show && !log[signatureId]) {
            setSelectedLog(log);
        } else {
            setSelectedLog('');
        };
    };

    const handleSort = (fieldId) => {
        setSort(prev => {
            if (prev.field === fieldId) {
                return {
                    ...prev,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            } else {
                return {
                    field: fieldId,
                    direction: 'asc'
                };
            };
        });
    };

    // desktop
    const sortedLogs = useMemo(() => {
        if (!logs) return [];

        const filteredLogs = logs.filter(l => deletedToggle ? l?.deleted : !l?.deleted);

        return [...filteredLogs].sort((a, b) => {
            const valA = a[sort.field];
            const valB = b[sort.field];

            const fieldType = fields.find(col => col.id === sort.field)?.type;

            let comparison = 0;

            // this will handle different data type sorting
            if (fieldType === 'date') {
                comparison = new Date(valA).getTime() - new Date(valB).getTime();
            } else if (fieldType === 't9wW') {
                // sorted by joining array of values
                const strA = Array.isArray(valA) ? valA.join(', ') : '';
                const strB = Array.isArray(valB) ? valB.join(', ') : '';
                comparison = strA.localeCompare(strB, undefined, { sensitivity: 'base' });
            } else if (fieldType === 'PjX1') {
                // sorted as signed vs not signed
                const boolA = !!valA;
                const boolB = !!valB;
                comparison = (boolA === boolB) ? 0 : (boolA ? 1 : -1);
            } else if (fieldType === 'BaPx') {
                const numA = parseFloat(valA);
                const numB = parseFloat(valB);

                if (isNaN(numA) && isNaN(numB)) {
                    comparison = 0;
                } else if (isNaN(numA)) {
                    comparison = 1;
                } else if (isNaN(numB)) {
                    comparison = -1;
                } else {
                    comparison = numA - numB;
                }
            } else if (typeof valA === 'string' && typeof valB === 'string') {
                comparison = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
            } else if (typeof valA === 'number' && typeof valB === 'number') {
                comparison = valA - valB;
            } else {
                const strA = String(valA || '').toLowerCase();
                const strB = String(valB || '').toLowerCase();
                comparison = strA.localeCompare(strB);
            };

            return sort.direction === 'asc' ? comparison : -comparison;
        });
    }, [logs, sort, deletedToggle, fields]);

    // mobile
    const filteredLogs = useMemo(() => {
        if (!searchTerm) return sortedLogs;
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        return sortedLogs?.filter(log =>
            fields?.some(field => {
                const logValue = log[field.id];
                if (logValue === undefined || logValue === null) return false;

                let valueToSearch = '';
                switch (configTypes?.[field.type]?.type) {
                    case 'checkbox':
                        valueToSearch = Array.isArray(logValue) ? logValue.join(', ') : String(logValue);
                        break;
                    case 'date':
                        valueToSearch = new Date(logValue).toLocaleString();
                        break;
                    case 'datetime-local':
                        valueToSearch = convertDateTimeFormat(logValue);
                        break;
                    case 'signature':
                        valueToSearch = logValue ? 'Signed' : 'No Signature';
                        break;
                    case 'address':
                        valueToSearch = formatAddress(logValue);
                        break;
                    default:
                        valueToSearch = String(logValue);
                        break;
                };
                return valueToSearch.toLowerCase().includes(lowercasedSearchTerm);
            })
        );
    }, [searchTerm, sortedLogs, fields]);

    if (error) return (<div className={styles.error}>{error}</div>);

    if (!logs) return (<></>);

    if (selectedLog) {
        return (<Log authenticatedUser={authenticatedUser} selectedLog={selectedLog} setSelectedLog={setSelectedLog} selectedSignature={selectedSignature} fields={fields} allFields={allFields} editLogEntry={editLogEntry} setEditLogEntry={setEditLogEntry} id={id} qrType={qrType} configTypes={configTypes} dataType={dataType} />)
    };

    const handleReturn = () => {
        if (isMobile) {
            return (
                <div className={styles.mobileLayoutWrapper}>
                    <div className={styles.searchBarWrap}>
                        <div className={styles.searchBarContainer}>
                            <input
                                type='text'
                                placeholder='Search logs...'
                                className={styles.logSearchBar}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <i className={`fas fa-search ${styles.searchIcon}`} />
                        </div>
                    </div>
                    <div className={styles.cardContainer}>
                        {filteredLogs?.length > 0 ?
                            filteredLogs?.map((log, index) => (
                                <div
                                    className={`${styles.logCard} ${deletedToggle ? styles.deletedLog : ''}`}
                                    key={index}
                                    onClick={() => handleGetSignature(log, selectedLog?.date === log?.date ? false : true)}
                                >
                                    <div className={styles.cardItem}>
                                        <span className={styles.cardLabel}>Timestamp:</span>
                                        <OverlayTriggerWrap data={new Date(log['date']).toLocaleString()} >
                                            <span className={styles.cardValue}>
                                                {new Date(log['date']).toLocaleString()}
                                            </span>
                                        </OverlayTriggerWrap>
                                    </div>
                                    {fields?.map((field, colIndex) => {
                                        let displayValue;
                                        let fieldLabel = field.type === 'date' ? 'Timestamp' : field.name;
                                        // The log timestamp is already rendered as the first card item above.
                                        // Skip the schema's timestamp field so mobile matches the desktop table.
                                        if (field.type === 'date') {
                                            return <Fragment key={colIndex} />
                                        }
                                        switch (configTypes?.[field?.type]?.type) {
                                            case 'checkbox':
                                                if (configTypes?.[field?.type]?.multiple) {
                                                    displayValue = log[field?.id]?.length > 0 ? log[field?.id]?.join(', ') : 'N/A';
                                                } else {
                                                    displayValue = log[field?.id];
                                                };
                                                break;
                                            case 'date':
                                                displayValue = new Date(log[field?.id]).toLocaleString();
                                                break;
                                            case 'datetime-local':
                                                displayValue = convertDateTimeFormat(log[field?.id]);
                                                break;
                                            case 'signature':
                                                displayValue = log[field?.id] ? 'Signed' : 'No Signature';
                                                break;
                                            case 'header':
                                            case 'paragraph':
                                                displayValue = field?.value || 'N/A';
                                                break;
                                            case 'file': {
                                                const f = Array.isArray(log?.[field?.id]) ? log?.[field?.id]?.[0] : log?.[field?.id];
                                                displayValue = f?.url ? 'File uploaded' : 'No file';
                                                break;
                                            }
                                            case 'address':
                                                displayValue = formatAddress(log[field?.id]);
                                                break;
                                            default:
                                                displayValue = log[field.id] || 'N/A';
                                                break;
                                        };

                                        const hasEdits = log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0;
                                        const showAsterisk = hasEdits && colIndex === 0;
                                        
                                        if (configTypes?.[field?.type]?.type === 'file') {
                                            const f = Array.isArray(log?.[field?.id]) ? log?.[field?.id]?.[0] : log?.[field?.id];
                                            const url = f?.url;
                                            const name = f?.name;
                                            return (
                                                <div className={styles.cardItem} key={colIndex}>
                                                    <span className={styles.cardLabel}>{fieldLabel}:</span>
                                                    <span className={styles.cardValue}>
                                                        {showAsterisk && <span className={styles.editIndicator}>*</span>}
                                                        {url ? <img className={styles.fileThumb} src={url} alt={name || 'File'} /> : 'No file'}
                                                    </span>
                                                </div>
                                            );
                                        }
                                        if (configTypes?.[field?.type]?.type === 'checkbox' && !configTypes?.[field?.type]?.multiple) {
                                            return (
                                                <div className={styles.cardItem} key={colIndex}>
                                                    <span className={styles.cardLabel}>{fieldLabel}:</span>
                                                    <span className={styles.cardValue}>
                                                        {showAsterisk && <span className={styles.editIndicator}>*</span>}
                                                        {log[field?.id] ? <i className='far fa-check-square' /> : <i className='far fa-square' />}
                                                    </span>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div className={styles.cardItem} key={colIndex}>
                                                <span className={styles.cardLabel}>{fieldLabel}:</span>
                                                <OverlayTriggerWrap key={index} data={displayValue} >
                                                    <span className={styles.cardValue}>
                                                        {showAsterisk && <span className={styles.editIndicator}>*</span>}
                                                        {displayValue}
                                                    </span>
                                                </OverlayTriggerWrap>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                            :
                            <div className={styles.none}>No logs found</div>
                        }
                    </div>
                </div>
            );
        } else {
            return (
                <div className={styles.table}>
                    <div className={styles.logHeaders}>
                        <OverlayTriggerWrap data={'Timestamp'} >
                            <div
                                className={`${styles.logType} ${styles.sortableHeader}`}
                                style={{ width: columnWidth }}
                            >
                                <span>Timestamp</span>
                            </div>
                        </OverlayTriggerWrap>
                        {visibleFields?.map((field, index) => {
                            const isSortedColumn = sort.field === field.id;
                            const headerText = field?.name;
                            return (
                                <OverlayTriggerWrap key={index} data={headerText} >
                                    <div
                                        className={`${styles.logType} ${styles.sortableHeader}`}
                                        style={{ width: columnWidth }}
                                        onClick={() => handleSort(field.id)}
                                    >
                                        <span>{headerText}</span>
                                        {isSortedColumn && (
                                            <i className={sort.direction === 'asc' ? `far fa-caret-up ms-2 ${styles.logTypeI}` : `far fa-caret-down ms-2 ${styles.logTypeI}`} />
                                        )}
                                    </div>
                                </OverlayTriggerWrap>
                            );
                        })}
                    </div>
                    {sortedLogs?.length > 0 ?
                        sortedLogs?.map((log, index) => {
                            return (
                                <div className={deletedToggle ? `${styles.logRow} ${styles.deletedLog}` : `${styles.logRow}`} key={index} onClick={() => handleGetSignature(log, selectedLog?.date === log?.date ? false : true)}>
                                    <OverlayTriggerWrap data={log['date'] && new Date(log['date'])?.toLocaleString() || ''} >
                                        <div className={styles.logType} style={{ width: columnWidth }}>
                                            <span>{new Date(log['date']).toLocaleString()}</span>
                                        </div>
                                    </OverlayTriggerWrap>
                                    {visibleFields?.map((field, index) => {
                                        switch (configTypes[field?.type]?.type) {
                                            case 'checkbox':
                                                if (configTypes[field?.type]?.multiple) {
                                                    return (
                                                        <OverlayTriggerWrap key={index} data={log?.[field?.id]} >
                                                            <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                                <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits || {})?.length > 0 && index === 0) ? '*' : ''}{log[field?.id]?.length > 0 ? log[field?.id]?.join(', ') : ''}</span>
                                                            </div>
                                                        </OverlayTriggerWrap>
                                                    );
                                                } else {
                                                    return (
                                                        <OverlayTriggerWrap key={index} data={log?.[field?.id]} >
                                                            <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                                <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{log[field.id] ? <i className='far fa-check-square' /> : <i className='far fa-square' />}</span>
                                                            </div>
                                                        </OverlayTriggerWrap>
                                                    );
                                                };
                                            case 'date':
                                                return (
                                                    <OverlayTriggerWrap key={index} data={log?.[field?.id] && new Date(log?.[field?.id])?.toLocaleString() || ''} >
                                                        <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                            <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{new Date(log[field?.id]).toLocaleString()}</span>
                                                        </div>
                                                    </OverlayTriggerWrap>
                                                );
                                            case 'datetime-local':
                                                return (
                                                    <OverlayTriggerWrap key={index} data={log?.[field?.id] && new Date(log?.[field?.id])?.toLocaleString() || ''} >
                                                        <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                            <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{convertDateTimeFormat(log[field?.id])}</span>
                                                        </div>
                                                    </OverlayTriggerWrap>
                                                );
                                            case 'signature':
                                                return (
                                                    <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                        <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{log[field?.id] ? 'Signed' : 'No Signature'}</span>
                                                    </div>
                                                );
                                            case 'header':
                                            case 'paragraph':
                                                return (
                                                    <OverlayTriggerWrap key={index} data={field?.value || ''} >
                                                        <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                            <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{field?.value || 'N/A'}</span>
                                                        </div>
                                                    </OverlayTriggerWrap>
                                                );
                                            case 'file': {
                                                const f = Array.isArray(log?.[field?.id]) ? log?.[field?.id]?.[0] : log?.[field?.id];
                                                const url = f?.url;
                                                const name = f?.name;
                                                return (
                                                    <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                        <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}</span>
                                                        {url ? <img className={styles.fileThumb} src={url} alt={name || 'File'} /> : <span>N/A</span>}
                                                    </div>
                                                );
                                            }
                                            case 'address': {
                                                const formattedAddress = formatAddress(log?.[field?.id]);
                                                return (
                                                    <OverlayTriggerWrap key={index} data={formattedAddress} >
                                                        <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                            <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{formattedAddress}</span>
                                                        </div>
                                                    </OverlayTriggerWrap>
                                                );
                                            }
                                            default:
                                                return (
                                                    <OverlayTriggerWrap key={index} data={log?.[field?.id]} >
                                                        <div className={styles.logType} key={index} style={{ width: columnWidth }}>
                                                            <span>{(log?.hasOwnProperty('edits') && Object?.keys(log?.edits)?.length > 0 && index === 0) ? '*' : ''}{log[field.id]}</span>
                                                        </div>
                                                    </OverlayTriggerWrap>
                                                );
                                        };
                                    }
                                    )}
                                </div>
                            );
                        })
                        :
                        <div className={styles.none}>No logs found</div>
                    }
                </div>
            );
        };
    };

    return (
        <>
            <div className={styles.header}>
                <h2>{qrObj?.nickname}</h2>
                {qrData?.description && (<div className={styles.description}>{qrData.description}</div>)}
            </div>
            {qrData?.files?.length > 0 && (
                <div className={styles.fileContainer}>
                    <TrackerCarousel files={qrData.files} />
                </div>
            )}
            <div className={styles.btnContainer}>
                <button className={styles.btn} onClick={() => setShowAddLog(true)} >{qrType === 'tracker' ? 'Add Log' : 'Add Response'}</button>
            </div>
            <div className={styles.logContainer} >
                <div className={styles.logSection} >
                    {handleReturn()}
                    <div className={styles.key}>
                        <span>* indicates log has been edited</span>
                        {(authenticatedUser && logs?.filter(l => l?.deleted)?.length) ? <span className={styles.deletedToggle} onClick={() => setDeletedToggle(prev => !prev)} >{deletedToggle ? 'Hide Deleted' : 'Show Deleted'}</span> : <></>}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Table;
